/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-eq-null */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import useDebounce from '../../../hooks/useDebouncedKeyword';
import updateReport from '../../../actions/api/report/updateReport';
import { SUCCESS_API_GET_REPORT_DETAIL } from '../../../actions/actionTypes';
import { showToast } from '../../../lib/tools';
import getReportDetail from '../../../actions/api/report/getReportDetail';
import checkReportStatus from '../functions/checkReportStatus';
import { REPORT_UPDATE_ERROR_CODE } from '../../../lib/constants';

/**
 * 報告書のヘッダー情報を自動保存するカスタムフック。
 * @param {string} apiToken - APIトークン。
 * @param {string} targetGroupId - 現在選択中のグループID。
 * @param {string} reportId - 編集中の報告書ID。
 * @param {string} updatedAt - 報告書の最終更新日時。
 * @param {Function} updateAutoSaveStatus - 自動保存中かどうかを示す真偽値を設定する関数。ReportEditPageで使用。
 * @returns {Object} フックの戻り値。
 * @returns {Function} setReportTitle - 報告書のタイトルを設定する関数。
 * @returns {Function} setCreateDate - 報告書の作成日を設定する関数。
 * @returns {boolean} isSaving - 保存中かどうかを示す真偽値。
 * @returns {boolean} showReportNameConflictDialog - 報告書件名の競合ダイアログを表示するかどうかを示す真偽値。
 * @returns {Function} setShowReportNameConflictDialog - 報告書名の競合ダイアログを表示するかどうかを設定する関数。
 * @returns {boolean} showCreatedDateConflictDialog - 作成日の競合ダイアログを表示するかどうかを示す真偽値。
 * @returns {Function} setShowCreatedDateConflictDialog - 作成日の競合ダイアログを表示するかどうかを設定する関数。
 * @returns {string} debouncedTitle - デバウンスされた報告書のタイトル。
 * @returns {string} debouncedDate - デバウンスされた報告書の作成日。
 */
const useAutoSave = (
  apiToken,
  targetGroupId,
  reportId,
  updatedAt,
  updateAutoSaveStatus,
) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [reportTitle, setReportTitle] = useState('');
  const debouncedTitle = useDebounce({ value: reportTitle, delay: 3000 });

  const [createDate, setCreateDate] = useState('');
  const debouncedDate = useDebounce({ value: createDate, delay: 3000 });

  const [isSaving, setIsSaving] = useState(false);
  const [showReportNameConflictDialog, setShowReportNameConflictDialog] =
    useState(false);
  const [showCreatedDateConflictDialog, setShowCreatedDateConflictDialog] =
    useState(false);

  useEffect(() => {
    setIsSaving(true);
    updateAutoSaveStatus('isReportTitleAutoSaving', true);
  }, [reportTitle]);

  useEffect(() => {
    setIsSaving(true);
    updateAutoSaveStatus('isReportCreateDateAutoSaving', true);
  }, [createDate]);

  useEffect(() => {
    async function saveTitle() {
      if (debouncedTitle !== '') {
        const result = await dispatch(
          updateReport(
            apiToken,
            { name: debouncedTitle },
            targetGroupId,
            reportId,
            updatedAt,
            SUCCESS_API_GET_REPORT_DETAIL,
          ),
        );
        if (result?.status === 200) {
          dispatch(getReportDetail(apiToken, targetGroupId, reportId));
          showToast(
            intl.formatMessage({
              id: 'toast.message.report.autoSave',
            }),
          );
        }
        // ステータスを完了に変更された場合の処理
        if (
          result?.response?.status === 400 &&
          result?.response?.data?.errorCode ===
            REPORT_UPDATE_ERROR_CODE.UNEDITABLE_CONTENT
        ) {
          await checkReportStatus(apiToken, targetGroupId, reportId, dispatch);
        }
        // 競合エラーの場合の処理
        if (result?.response?.status === 412) {
          setShowReportNameConflictDialog(true);
        }
      }
      setIsSaving(false);
      updateAutoSaveStatus('isReportTitleAutoSaving', false);
    }
    saveTitle();
  }, [debouncedTitle]);

  useEffect(() => {
    async function saveDate() {
      if (debouncedDate) {
        const result = await dispatch(
          updateReport(
            apiToken,
            { createDate: debouncedDate },
            targetGroupId,
            reportId,
            updatedAt,
            SUCCESS_API_GET_REPORT_DETAIL,
          ),
        );
        if (result?.status === 200) {
          dispatch(getReportDetail(apiToken, targetGroupId, reportId));
          showToast(
            intl.formatMessage({ id: 'toast.message.report.autoSave' }),
          );
        }
        // ステータスを完了に変更された場合の処理
        if (
          result?.response?.status === 400 &&
          result?.response?.data?.errorCode ===
            REPORT_UPDATE_ERROR_CODE.UNEDITABLE_CONTENT
        ) {
          await checkReportStatus(apiToken, targetGroupId, reportId, dispatch);
        }
        // 競合エラーの場合の処理
        if (result?.response?.status === 412) {
          setShowCreatedDateConflictDialog(true);
        }
      }
      setIsSaving(false);
      updateAutoSaveStatus('isReportCreateDateAutoSaving', false);
    }
    saveDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDate]);

  return {
    setReportTitle,
    setCreateDate,
    isSaving,
    showReportNameConflictDialog,
    setShowReportNameConflictDialog,
    showCreatedDateConflictDialog,
    setShowCreatedDateConflictDialog,
    debouncedTitle,
    debouncedDate,
  };
};

export default useAutoSave;
